const SOCIAL_QR_PLATFORM_CODES = ['douyin', 'kuaishou', 'weixin_channels'];

const pickSingleOnlineAgentId = (agents = []) => {
  const ids = Array.from(new Set(
    (Array.isArray(agents) ? agents : [])
      .filter((agent) => agent?.is_online === true)
      .map((agent) => String(agent?.agent_id || '').trim())
      .filter(Boolean)
  ));
  return ids.length === 1 ? ids[0] : undefined;
};

const tryAutoBindNewAccountAgent = async ({
  accountId,
  initialAgentId,
  operatorId,
  upsertAgentBinding,
  agentList,
  message,
  resolveErrorMessage,
}) => {
  const explicit = String(initialAgentId || '').trim();
  if (explicit) return explicit;
  if (!accountId || typeof upsertAgentBinding !== 'function') return undefined;

  const candidateAgentId = pickSingleOnlineAgentId(agentList);
  if (!candidateAgentId) return undefined;

  try {
    const bindResp = await upsertAgentBinding({
      account_id: accountId,
      agent_id: candidateAgentId,
      operator_id: operatorId || undefined,
      role: 'primary',
      priority: 1,
      enabled: true,
      set_as_primary: true,
      meta: { source: 'account_create_auto_bind' },
    });
    if (bindResp?.status === 'success') {
      return String(bindResp?.primary_agent_id || candidateAgentId).trim() || undefined;
    }
    message.warning(resolveErrorMessage(bindResp?.error_message, '检测到在线 Agent，但自动绑定失败，请手动绑定后再登录'));
  } catch (error) {
    message.warning(resolveErrorMessage(error?.message || String(error), '检测到在线 Agent，但自动绑定失败，请手动绑定后再登录'));
  }
  return undefined;
};

const buildBasePayload = ({ values, normalizeOptionalString, parseCommaList, parseJsonObject }) => {
  const basePayload = {
    platform: normalizeOptionalString(values.platform),
    account_name: normalizeOptionalString(values.account_name),
    account_type: normalizeOptionalString(values.account_type),
    status: normalizeOptionalString(values.status),
    auth_config_id: normalizeOptionalString(values.auth_config_id),
    operator_user_ids: parseCommaList(values.operator_user_ids),
    operator_user_names: parseCommaList(values.operator_user_names),
  };

  const platformConfig = parseJsonObject(values.platform_config);
  if (platformConfig !== undefined) basePayload.platform_config = platformConfig;

  return { basePayload, platformConfig };
};

const handleCreateSave = async ({
  values,
  basePayload,
  refreshQuota,
  resolveAccountCreateBlock,
  quota,
  message,
  redirectToBilling,
  normalizeOptionalString,
  createPublishAccount,
  reloadCurrentAccountList,
  xhsMcpEnsure,
  setXhsProvisioning,
  setXhsProvisionAccount,
  setSocialProvisionAccount,
  upsertAgentBinding,
  agentList,
  operatorId,
  closeDetailDrawer,
  resolveErrorMessage,
}) => {
  const latestQuota = await refreshQuota();
  const createBlock = resolveAccountCreateBlock(latestQuota || quota);
  if (createBlock.blocked) {
    message.warning(`${createBlock.message} 正在前往付费订阅页面...`);
    redirectToBilling();
    return;
  }

  const payload = {
    ...basePayload,
    account_id: normalizeOptionalString(values.account_id),
  };

  if (!payload.platform || !payload.account_name) {
    message.error('请填写平台与账号名称');
    return;
  }

  message.loading({ content: '正在保存...', key: 'saveAccount' });
  const response = await createPublishAccount(payload);
  if (response.status !== 'success') {
    message.error({ content: resolveErrorMessage(response.error_message, '保存失败'), key: 'saveAccount' });
    return;
  }

  message.success({ content: `保存成功（${response.action || 'success'}）`, key: 'saveAccount' });
  await reloadCurrentAccountList();
  refreshQuota().catch(() => {});

  const platformCode = String(response?.account?.platform_code || '').trim();
  const platformDisplay = String(values?.platform || response?.account?.platform || '').trim();
  const accountId = response?.account_id;
  if ((platformCode === 'xhs' || platformDisplay === '小红书') && accountId) {
    try {
      setXhsProvisioning(true);
      message.loading({ content: '正在启动小红书 MCP...', key: 'xhsProvision' });
      const preferPortRaw = values?.xhs_prefer_port;
      const preferPort = preferPortRaw ? Number(preferPortRaw) : undefined;
      const ensureResp = await xhsMcpEnsure({
        account_id: accountId,
        ...(Number.isFinite(preferPort) ? { prefer_port: preferPort } : {}),
      });
      if (ensureResp?.status === 'success') {
        message.success({ content: '小红书 MCP 已启动，请扫码登录', key: 'xhsProvision' });
        setXhsProvisionAccount({
          account_id: accountId,
          account_name: response?.account?.account_name || values?.account_name || accountId,
          platform: response?.account?.platform || platformDisplay || '小红书',
          platform_code: 'xhs',
        });
      } else {
        const ensureMsg = resolveErrorMessage(ensureResp?.error_message || ensureResp?.reason, '启动 MCP 失败');
        message.error({ content: ensureMsg, key: 'xhsProvision' });
      }
    } catch (e) {
      message.error({ content: e?.message || String(e), key: 'xhsProvision' });
    } finally {
      setXhsProvisioning(false);
    }
    return;
  }

  closeDetailDrawer();

  try {
    const createPlatformCode = String(response?.account?.platform_code || '').trim();
    const createdAccountId = response?.account_id;
    const loginAfterCreate = values?.login_after_create;
    const initialPrimaryAgentId = String(response?.account?.primary_agent_id || response?.account?.agent_id || '').trim();
    if (
      createdAccountId
      && loginAfterCreate !== false
      && SOCIAL_QR_PLATFORM_CODES.includes(createPlatformCode)
    ) {
      const primaryAgentId = await tryAutoBindNewAccountAgent({
        accountId: createdAccountId,
        initialAgentId: initialPrimaryAgentId,
        operatorId,
        upsertAgentBinding,
        agentList,
        message,
        resolveErrorMessage,
      });
      setSocialProvisionAccount({
        account_id: createdAccountId,
        account_name: response?.account?.account_name || createdAccountId,
        platform: response?.account?.platform || createPlatformCode,
        platform_code: createPlatformCode,
        agent_id: primaryAgentId || undefined,
        primary_agent_id: primaryAgentId || undefined,
      });
    }
  } catch (_) {
    // ignore social provision suggestion errors
  }
};

const handleEditSave = async ({
  values,
  basePayload,
  platformConfig,
  normalizeOptionalString,
  message,
  updatePublishAccount,
  resolveErrorMessage,
  setDataSource,
  recentlyUpdatedAccountsRef,
  closeDetailDrawer,
  reloadCurrentAccountList,
}) => {
  const accountId = normalizeOptionalString(values.account_id);
  if (!accountId) {
    message.error('缺少 account_id，无法更新');
    return;
  }

  const nextLocal = Object.fromEntries(
    Object.entries({
      account_id: accountId,
      ...basePayload,
      ...(platformConfig !== undefined ? { platform_config: platformConfig } : {}),
    }).filter(([, v]) => v !== undefined)
  );

  const payload = Object.fromEntries(
    Object.entries({
      account_id: accountId,
      ...basePayload,
    }).filter(([, v]) => v !== undefined)
  );

  message.loading({ content: '正在更新...', key: 'saveAccount' });
  const response = await updatePublishAccount(payload);
  if (response.status !== 'success') {
    message.error({ content: resolveErrorMessage(response.error_message, '更新失败'), key: 'saveAccount' });
    return;
  }

  message.success({ content: '更新成功', key: 'saveAccount' });
  setDataSource((prev) => (prev || []).map((item) => (
    item?.account_id === accountId ? { ...item, ...nextLocal } : item
  )));
  recentlyUpdatedAccountsRef.current.set(accountId, { ts: Date.now(), patch: nextLocal });
  closeDetailDrawer();
  await new Promise((r) => setTimeout(r, 400));
  await reloadCurrentAccountList();
};

export const buildHandleAccountSave = ({
  detailForm,
  drawerMode,
  normalizeOptionalString,
  parseCommaList,
  parseJsonObject,
  refreshQuota,
  resolveAccountCreateBlock,
  quota,
  message,
  redirectToBilling,
  createPublishAccount,
  updatePublishAccount,
  reloadCurrentAccountList,
  xhsMcpEnsure,
  setXhsProvisioning,
  setXhsProvisionAccount,
  setSocialProvisionAccount,
  upsertAgentBinding,
  agentList,
  operatorId,
  closeDetailDrawer,
  resolveErrorMessage,
  setDataSource,
  recentlyUpdatedAccountsRef,
}) => async () => {
  try {
    const values = await detailForm.validateFields();
    const { basePayload, platformConfig } = buildBasePayload({
      values,
      normalizeOptionalString,
      parseCommaList,
      parseJsonObject,
    });

    if (drawerMode === 'create') {
      await handleCreateSave({
        values,
        basePayload,
        refreshQuota,
        resolveAccountCreateBlock,
        quota,
        message,
        redirectToBilling,
        normalizeOptionalString,
        createPublishAccount,
        reloadCurrentAccountList,
        xhsMcpEnsure,
        setXhsProvisioning,
        setXhsProvisionAccount,
        setSocialProvisionAccount,
        upsertAgentBinding,
        agentList,
        operatorId,
        closeDetailDrawer,
        resolveErrorMessage,
      });
      return;
    }

    await handleEditSave({
      values,
      basePayload,
      platformConfig,
      normalizeOptionalString,
      message,
      updatePublishAccount,
      resolveErrorMessage,
      setDataSource,
      recentlyUpdatedAccountsRef,
      closeDetailDrawer,
      reloadCurrentAccountList,
    });
  } catch (error) {
    if (error?.message?.includes('platform_config JSON 解析失败')) {
      message.error(error.message);
      return;
    }
    if (error?.errorFields) return;
    console.error('保存失败:', error);
    const backendMessage = resolveErrorMessage(
      error?.detail?.error_message || error?.detail?.message || error?.message,
      '保存失败，请稍后重试'
    );
    message.error(backendMessage);
  }
};
